import { Tabs, Tag, Tooltip, Col } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DateTime } from 'luxon';

import CustomFilter from '@portal/components/CustomFilter/CustomFilter';
import DataTable from '@portal/components/DataTable/DataTable';
import PanelContentBreadcrumb from '@portal/components/PanelContentBreadcrumb/PanelContentBreadcrumb';
import PanelContentHeader from '@portal/components/PanelContentHeader/PanelContentHeader';
import UploadModal from '@portal/components/UploadModal/UploadModal';
import InvalidDataModal from '@portal/components/InvalidDataModal/InvalidDataModal';
import CsvFileUploader from '@portal/components/CsvFileUploader/CsvFileUploader';
import CSVStatusTooltip from '@portal/components/CSVStatusTooltip/CSVStatusTooltip';

import { getRouteStackPath } from '@portal/config/routes';
import { translate } from '@portal/services/i18n';
import { AdvancedFilterType } from '@portal/enum/advancedFilter';
import * as MessageService from '@portal/services/message';
import { queryStringToObject } from '@portal/services/queryString';

import { EmployeesActions, SettingsActions } from '@portal/actions';
import {
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  LikeOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import DownloadIcon from '@portal/assets/svg/employees-buttons/ic_download.svg?react';
import useReduxState from '@portal/hooks/useReduxState';
import {
  EmployeeAccountType,
  EmployeeCSVTab,
  FileAllowed,
  UploadCSVStatus,
  UploadStatus,
} from '@portal/models/enumerators/employees';

import { employees } from '@portal/config/app.json';

const initialValues: advancedFilterModels.baseFilter = {
  orderBy: 'createdAt',
  page: 0,
  pageSize: 10,
  sort: 'desc',
};

const initialForm: models.PaymentEmployeeForm = {
  file: null,
  data: [],
  type: EmployeeAccountType.PF,
};

const EmployeesCSV = () => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(initialForm);
  const [visible, setVisible] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState(initialValues);
  const [openModal, setOpenModal] = useState(false);
  const [tabKey, setTabKey] = useState<string>(EmployeeCSVTab.PF);
  const filterParams = new URLSearchParams(location.search);

  const onSearch = (filters: advancedFilterModels.EmployeesPageFilter) => {
    const queryParams = queryStringToObject();
    const newFilter = {
      ...queryParams,
      ...filters,
    };
    setAdvancedFilters(newFilter);
    dispatch(
      EmployeesActions.getUploadLog(
        newFilter,
        tabKey === EmployeeCSVTab.PF
          ? EmployeeAccountType.PF
          : EmployeeAccountType.PJ
      )
    );
    dispatch(SettingsActions.getSettings());
  };

  const {
    employees: { uploadLog, logCount, paymentStatus },
    settings: { settings },
  } = useReduxState();

  const uploadPayment = () => {
    if (form.file === null) {
      return MessageService.error(
        'PAGES.EMPLOYEES.EMPLOYEES_PAYMENT.MESSAGE.NO_FILE_UPLOADED',
        { domain: 'validation' }
      );
    }

    if (form?.file?.type !== FileAllowed.CSV) {
      return MessageService.error(
        'PAGES.EMPLOYEES.EMPLOYEES_PAYMENT.MESSAGE.FILE_NOT_ALLOWED',
        { domain: 'validation' }
      );
    }

    dispatch(EmployeesActions.uploadEmployeesList(form));
  };

  useEffect(() => {
    if (paymentStatus === UploadStatus.SUCCESS) {
      setVisible(true);
    }

    if (paymentStatus === UploadStatus.ERROR) {
      setVisible(true);
      setHasError(true);

      return () => {
        setHasError(false);
      };
    }

    form.data?.length !== 0 && setOpenModal(true);
  }, [form?.data, paymentStatus, dispatch]);

  const customStatus = (status: UploadCSVStatus) => {
    switch (status) {
      case UploadCSVStatus.PROCESSED:
        return (
          <Tag
            icon={<ClockCircleOutlined />}
            className="employees-upload__status"
            color="blue"
          >
            {translate('PAGES.EMPLOYEES.EMPLOYEES_CSV.STATUS.PROCESSED')}
          </Tag>
        );
      case UploadCSVStatus.CONCLUDED:
        return (
          <Tag
            icon={<LikeOutlined />}
            className="employees-upload__status"
            color="success"
          >
            {translate('PAGES.EMPLOYEES.EMPLOYEES_CSV.STATUS.CONCLUDED')}
          </Tag>
        );
      case UploadCSVStatus.PROCESSING:
        return (
          <Tag
            icon={<SyncOutlined spin />}
            className="employees-upload__status"
            color="blue"
          >
            {translate('PAGES.EMPLOYEES.EMPLOYEES_CSV.STATUS.PROCESSING')}
          </Tag>
        );
      case UploadCSVStatus.ERROR:
        return (
          <Tag
            icon={<ExclamationCircleOutlined />}
            className="employees-upload__status"
            color="error"
          >
            {translate('PAGES.EMPLOYEES.EMPLOYEES_CSV.STATUS.ERROR')}
          </Tag>
        );
    }
  };

  const onChangeTab = (key: string) => {
    setTabKey(key);

    switch (key) {
      case EmployeeCSVTab.PF:
        dispatch(
          EmployeesActions.getUploadLog(initialValues, EmployeeAccountType.PF)
        );
        break;
      case EmployeeCSVTab.PJ:
        dispatch(
          EmployeesActions.getUploadLog(initialValues, EmployeeAccountType.PJ)
        );
        break;
    }

    setAdvancedFilters(initialValues);
    setForm(initialForm);
  };

  return (
    <Col className="employees-upload">
      <div className="employees-upload__container">
        <PanelContentBreadcrumb
          items={[
            {
              active: true,
              title: translate('COMPONENTS.BREADCRUMB.HOME'),
              url: getRouteStackPath('HOME', 'HOME_PAGE'),
            },
            {
              active: true,
              title: translate('PAGES.EMPLOYEES.EMPLOYEES_PAGE.PAGE_TITLE'),
              url: getRouteStackPath('EMPLOYEES', 'EMPLOYEES_PAGE'),
            },
            {
              active: false,
              title: translate('PAGES.EMPLOYEES.EMPLOYEES_CSV.PAGE_TITLE'),
            },
          ]}
        />

        <PanelContentHeader
          pageTitle={translate('PAGES.EMPLOYEES.EMPLOYEES_CSV.PAGE_TITLE')}
          pageDescription={translate(
            'PAGES.EMPLOYEES.EMPLOYEES_CSV.PAGE_SUBTITLE'
          )}
        />

        <Tabs
          defaultActiveKey={EmployeeCSVTab.PF}
          activeKey={tabKey}
          onChange={(value: string) => onChangeTab(value)}
        >
          <Tabs.TabPane
            tab={translate('PAGES.EMPLOYEES.EMPLOYEES_PAGE.BUTTONS.PF')}
            key={EmployeeCSVTab.PF}
          >
            <CsvFileUploader
              form={form}
              setForm={setForm}
              uploadPayment={uploadPayment}
              templateLink={settings?.templatesLinkedAccount?.createAccountPf}
              handbook={settings?.bms?.handbookEmployee}
            />
          </Tabs.TabPane>
          {employees.allowPJImportCsv ? (
            <Tabs.TabPane
              tab={translate('PAGES.EMPLOYEES.EMPLOYEES_PAGE.BUTTONS.PJ')}
              key={EmployeeCSVTab.PJ}
            >
              <CsvFileUploader
                form={form}
                setForm={setForm}
                uploadPayment={uploadPayment}
                templateLink={settings?.templatesLinkedAccount?.createAccountPj}
                handbook={settings?.bms?.handbookEmployee}
                isEmployeePJ
              />
            </Tabs.TabPane>
          ) : null}
        </Tabs>

        <div className="mt-2">
          <CustomFilter
            fields={[
              {
                name: 'name',
                placeholder: translate(
                  'PAGES.EMPLOYEES.EMPLOYEES_CSV.LABEL.NAME'
                ),
                type: AdvancedFilterType.TEXT,
                defaultValue: filterParams.get('name') ?? '',
              },
              {
                name: 'status',
                placeholder: translate(
                  'PAGES.EMPLOYEES.EMPLOYEES_CSV.LABEL.STATUS'
                ),
                type: AdvancedFilterType.SELECT,
                defaultValue: filterParams.get('status') ?? '',
                options: [
                  {
                    value: '0',
                    name: translate(
                      'PAGES.EMPLOYEES.EMPLOYEES_CSV.STATUS.PROCESSED'
                    ),
                  },
                  {
                    value: '1',
                    name: translate(
                      'PAGES.EMPLOYEES.EMPLOYEES_CSV.STATUS.PROCESSING'
                    ),
                  },
                  {
                    value: '2',
                    name: translate(
                      'PAGES.EMPLOYEES.EMPLOYEES_CSV.STATUS.CONCLUDED'
                    ),
                  },
                  {
                    value: '3',
                    name: translate(
                      'PAGES.EMPLOYEES.EMPLOYEES_CSV.STATUS.ERROR'
                    ),
                  },
                ],
              },
              {
                name: 'date',
                placeholder: [
                  translate(
                    'PAGES.EMPLOYEES.EMPLOYEES_PAGE.ADVANCED_FILTER.CREATED_AT.START'
                  ),
                  translate(
                    'PAGES.EMPLOYEES.EMPLOYEES_PAGE.ADVANCED_FILTER.CREATED_AT.END'
                  ),
                ],
                type: AdvancedFilterType.RANGE_PICKER,
                defaultValue: filterParams.get('date') ?? '',
                disableFuture: true,
                onlyDate: true,
              },
            ]}
            onSearch={onSearch}
          />
        </div>

        <div>
          <DataTable
            columns={[
              {
                field: 'id',
                flex: 1,
                headerName: 'id',
                sortable: false,
                hide: true,
              },
              {
                field: 'fileName',
                flex: 2,
                headerName: translate(
                  'PAGES.EMPLOYEES.EMPLOYEES_CSV.LABEL.NAME'
                ),
                renderCell: ({ row }) => row?.fileName && <>{row.fileName}</>,
              },
              {
                field: 'createdAt',
                flex: 1,
                headerName: translate(
                  'PAGES.EMPLOYEES.EMPLOYEES_CSV.LABEL.DATE'
                ),
                renderCell: ({ row }) =>
                  DateTime.fromISO(row?.createdAt).toLocaleString(
                    DateTime.DATETIME_SHORT
                  ),
              },
              {
                field: 'status',
                flex: 1,
                headerName: translate(
                  'PAGES.EMPLOYEES.EMPLOYEES_CSV.LABEL.STATUS'
                ),
                renderCell: ({ row }) => customStatus(row?.status),
              },
              {
                field: 'action',
                headerName: '',
                flex: 1,
                renderCell: ({ row }) => (
                  <div className="account-payable__details action-buttons">
                    <button
                      className="transparent-button users__details--button"
                      type="button"
                      onClick={() => window.location.assign(row.url)}
                    >
                      <Tooltip
                        placement="bottom"
                        color="#fff"
                        title={
                          <div className="upload-payment__tooltip">
                            {translate(
                              'PAGES.EMPLOYEES.EMPLOYEES_PAYMENT.BUTTONS.DOWNLOAD'
                            )}
                          </div>
                        }
                      >
                        <DownloadIcon />
                      </Tooltip>
                    </button>
                    <Tooltip
                      title={
                        <CSVStatusTooltip
                          status={row.log}
                          fileName={row.fileName}
                        />
                      }
                      color="white"
                      placement="bottomLeft"
                      overlayInnerStyle={{ width: 283, height: 'auto' }}
                    >
                      <InfoCircleOutlined className="employees-upload__tooltip__icon-style" />
                    </Tooltip>
                  </div>
                ),
              },
            ]}
            rows={uploadLog || []}
            page={advancedFilters.page}
            pageSize={advancedFilters.pageSize}
            onChange={(filters) => {
              const searchFilters = {
                ...advancedFilters,
                ...filters,
              };
              setAdvancedFilters(searchFilters);
              onSearch(searchFilters);
            }}
            orderBy={advancedFilters.orderBy}
            sort={advancedFilters.sort}
            rowCount={logCount}
            alignLeft
          />
        </div>
      </div>
      <UploadModal
        setVisible={setVisible}
        visible={visible}
        error={hasError}
        advancedFilter={advancedFilters}
        type={form.type}
      />
      <InvalidDataModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        invalidData={form?.data}
        employeeType={form?.type}
      />
    </Col>
  );
};

export default EmployeesCSV;
